
export enum FeatureType {
    DURATION = 'duration',
    BOOLEAN = 'boolean',
    CREDITS = 'credits',
    CUSTOM = 'boolean',
    LIMIT = 'limit',
}

export enum DurationFeature {
    CLASSIC_JOB_DURATION = 'classic_job_duration',
    PREMIUM_JOB_DURATION = 'premium_job_duration',
    JOB_EDIT_DURATION = 'job_edit_duration',
}

export interface ConfigurationValue {
    type: FeatureType;
}

export interface DurationConfiguration extends ConfigurationValue {
    duration: number;
    unit: 'days' | 'hours';
    type: FeatureType.DURATION;
}

export enum BooleanFeature {
    BRANDS_MANAGEMENT = 'brands_management',
    JOB_TEMPLATES = 'job_templates',
    MULTICHANNEL_POSTING = 'multichannel_posting',
    APPLICATION_TRACKING = 'application_tracking',
    PUBLIC_PAGE = 'public_page',
    TALENT_POOL = 'talent_pool',
    TALENT_RATING = 'talent_rating',
    TALENT_NOTES = 'talent_notes',
    COLLABORATIVE_RECRUITMENT = 'collaborative_recruitment',
    HIERARCHICAL_RECRUITMENT = 'hierarchical_recruitment',
}

export interface BooleanConfiguration extends ConfigurationValue {
    enabled: boolean;
    type: FeatureType.BOOLEAN;
}

export enum LimitFeature {
    MAX_PHOTO_LIMIT = 'photos_credits',
    MAX_VIDEO_LIMIT = 'videos_credits',
}

export interface LimitConfiguration extends ConfigurationValue {
    limit: number;
    type: FeatureType.LIMIT;
}


export enum CreditsFeature {
    BRAND_CREDITS = 'brands_credits',
    ESTABLISHMENTS_CREDITS = 'establishments_credits',
    CLASSIC_JOBS_CREDITS = 'classic_jobs_credits',
    PREMIUM_JOBS_CREDITS = 'premium_jobs_credits',
    TOTAL_JOBS_CREDITS = 'total_jobs_credits',
    ADMIN_USERS_CREDITS = 'admin_users_credits',
    RECRUITER_USERS_CREDITS = 'recruiter_users_credits',
    ACCOUNTANT_USERS_CREDITS = 'accountant_users_credits',
}

export interface CreditsConfiguration extends ConfigurationValue {
    maximum: number;
    used: number;
    type: FeatureType.CREDITS;
}


export enum CustomFeature {
    CUSTOM = 'custom'
}

export interface CustomConfiguration extends ConfigurationValue {
    enabled: boolean;
    type: FeatureType.CUSTOM;
}

export const FeatureNames = {
    ...LimitFeature,
    ...DurationFeature,
    ...BooleanFeature,
    ...CreditsFeature,
    ...CustomFeature
};

export type FeatureNames = DurationFeature | BooleanFeature | CreditsFeature | CustomFeature | LimitFeature;

export type FeatureConfiguration = DurationConfiguration | BooleanConfiguration | CreditsConfiguration | CustomConfiguration | LimitConfiguration;


export const creditLabels: Record<CreditsFeature, string> = {
    [CreditsFeature.ADMIN_USERS_CREDITS]: "Nombre de crédits d'administrateurs utilisés/total",
    [CreditsFeature.ACCOUNTANT_USERS_CREDITS]: "Nombre de crédits de comptables utilisés/total",
    [CreditsFeature.RECRUITER_USERS_CREDITS]: "Nombre de crédits de members utilisés/total",
    [CreditsFeature.BRAND_CREDITS]: "Nombre de crédits de marque utilisés/total",
    [CreditsFeature.ESTABLISHMENTS_CREDITS]: "Nombre de crédits d'établissements utilisés/total",
    [CreditsFeature.CLASSIC_JOBS_CREDITS]: "Nombre de crédits d'emplois classiques utilisés/total",
    [CreditsFeature.PREMIUM_JOBS_CREDITS]: "Nombre de crédits d'emplois premium utilisés/total",
    [CreditsFeature.TOTAL_JOBS_CREDITS]: "Nombre total de publication d'offre utilisés/total",
}

export interface Feature {
    key: FeatureNames;
    name: string;
    description: string;
    configuration: FeatureConfiguration;
    active: boolean;
}


/**
 * Check if the user has enough credits to perform the action
 */
export const hasEnoughCredits = (features: Feature[] = [], featureName: CreditsFeature): boolean => {
    const featureConfig = features.find((feature) => feature.key === featureName)?.configuration as CreditsConfiguration;
    if (featureConfig?.maximum === -1) {
        return true;
    }
    if (featureConfig?.maximum <= featureConfig?.used) {
        // credits are not enough in french
        return false;
    }

    return true;
};

/**
 * Check if the user is able to access the feature
 */
export const ableToAccess = (features: Feature[], featureName: BooleanFeature): boolean => {
    const featureConfig = features?.find((feature) => feature.key === featureName)?.configuration as BooleanConfiguration;

    if (!featureConfig?.enabled) {
        return false;
    }

    return true;
};

/**
 * Check if the user is able to do the action within the duration
 */
export const ableToDoWithin = (features: Feature[] = [], featureName: DurationFeature, currentValue: Date): boolean => {
    const featureConfig = features.find((feature) => feature.key === featureName)?.configuration as DurationConfiguration;

    const currentDate = new Date();

    if (featureConfig?.unit === 'days') {
        const diff = currentDate.getDate() - currentValue.getDate();
        if (diff > featureConfig?.duration) {
            return false;
        }
    } else {
        const diff = currentDate.getHours() - currentValue.getHours();
        if (diff > featureConfig?.duration) {
            return false;
        }
    }

    return true;
};

export const getLimitFeature = (features: Feature[] = [], featureName: LimitFeature): LimitConfiguration => {
    return features.find((feature) => feature.key === featureName)?.configuration as LimitConfiguration;
}
